/**
 * TransmissionSection — Orchestrator for Outbound and Bank Responses tabs.
 * Outbound = PAIN_001 transmissions only.
 * Bank Responses = all other transmissions + bankResponses map entries (deduplicated).
 */
import { useMemo, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import Table from '@cloudscape-design/components/table';
import Tabs from '@cloudscape-design/components/tabs';
import { useAppSelector } from 'src/store/hooks';
import { PaymentTransmission, BankResponseEntry } from 'src/store/slices/payment/paymentState';
import { getOutboundColumns, getBankResponseColumns } from './transmissionColumns';
import { TransmissionDetailPanel } from './TransmissionDetailPanel';

/** Normalized row shape used by the Bank Responses table. */
interface BankResponseRow {
  executionArn: string | null;
  fileName: string | null;
  bank: string | null;
  beaconTransmissionState: string | null;
  messageType: string | null;
  type: string;
  responseCode: string | null;
  responseDescription: string | null;
}

/** Builds deduplicated bank response rows from transmissions + bankResponses map. */
const buildBankResponseRows = (
  transmissions: PaymentTransmission[],
  bankResponses: Record<string, BankResponseEntry[]>,
): BankResponseRow[] => {
  const rows: BankResponseRow[] = [];
  const seenArns = new Set<string>();

  // Non-PAIN_001 transmissions
  transmissions.forEach((t) => {
    if (t.messageType && t.messageType !== 'PAIN_001') {
      if (t.executionArn) seenArns.add(t.executionArn);
      rows.push({
        executionArn: t.executionArn,
        fileName: t.fileName,
        bank: t.bank,
        beaconTransmissionState: t.beaconTransmissionState,
        messageType: t.messageType,
        type: t.messageType,
        responseCode: null,
        responseDescription: null,
      });
    }
  });

  // Entries from bankResponses map (deduplicate by executionArn)
  Object.entries(bankResponses).forEach(([type, entries]) => {
    entries.forEach((entry) => {
      if (entry.executionArn && seenArns.has(entry.executionArn)) return;
      if (entry.executionArn) seenArns.add(entry.executionArn);
      rows.push({
        executionArn: entry.executionArn,
        fileName: entry.fileName,
        bank: entry.bank,
        beaconTransmissionState: entry.state,
        messageType: null,
        type,
        responseCode: entry.responseCode,
        responseDescription: entry.responseDescription,
      });
    });
  });

  return rows;
};

export const TransmissionSection = () => {
  const payment = useAppSelector(state => state.payment.data);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [activeTabId, setActiveTabId] = useState('outbound');

  const transmissions = useMemo(() => payment?.transmissions || [], [payment?.transmissions]);
  const bankResponses = useMemo(() => payment?.bankResponses || {}, [payment?.bankResponses]);

  const outboundRows = useMemo(
    () => transmissions.filter((t) => t.messageType === 'PAIN_001'),
    [transmissions],
  );

  const bankResponseRows = useMemo(
    () => buildBankResponseRows(transmissions, bankResponses),
    [transmissions, bankResponses],
  );

  if (outboundRows.length === 0 && bankResponseRows.length === 0) return null;

  const handleToggleDetails = (item: any) => {
    const isSameItem = item.executionArn != null
      && selectedItem?.executionArn === item.executionArn;
    setSelectedItem(isSameItem ? null : item);
  };

  const handleTabChange = ({ detail }: { detail: { activeTabId: string } }) => {
    setActiveTabId(detail.activeTabId);
    setSelectedItem(null);
  };

  const selectedArn = selectedItem?.executionArn || null;

  const renderWithPanel = (table: React.ReactNode) => {
    if (!selectedItem) return table;
    return (
      <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
        {table}
        <TransmissionDetailPanel item={selectedItem} onClose={() => setSelectedItem(null)} />
      </Grid>
    );
  };

  return (
    <Container header={<Header variant="h2">Transmissions</Header>}>
      <Tabs
        activeTabId={activeTabId}
        onChange={handleTabChange}
        tabs={[
          {
            id: 'outbound',
            label: `Outbound (${outboundRows.length})`,
            content: outboundRows.length > 0 ? renderWithPanel(
              <Table
                variant="embedded"
                sortingDisabled
                resizableColumns
                columnDefinitions={getOutboundColumns(selectedArn, handleToggleDetails)}
                items={outboundRows}
                empty={<Box textAlign="center" color="text-body-secondary">No transmissions</Box>}
              />
            ) : <Box color="text-body-secondary">No outbound transmissions</Box>,
          },
          {
            id: 'bank-responses',
            label: `Bank Responses (${bankResponseRows.length})`,
            content: bankResponseRows.length > 0 ? renderWithPanel(
              <Table
                variant="embedded"
                sortingDisabled
                resizableColumns
                columnDefinitions={getBankResponseColumns(selectedArn, handleToggleDetails)}
                items={bankResponseRows}
                empty={<Box textAlign="center" color="text-body-secondary">No bank responses</Box>}
              />
            ) : <Box color="text-body-secondary">No bank responses received yet</Box>,
          },
        ]}
      />
    </Container>
  );
};
